import { MdChevronLeft, MdChevronRight } from 'react-icons/md';

import { cn } from '../lib/cn';

export type PaginationProps = {
  /** Page courante, à partir de 1. */
  page: number;
  /** Nombre total d'éléments, toutes pages confondues. */
  total: number;
  /** Éléments par page. */
  limit: number;
  onChange: (page: number) => void;
  /** Nom des éléments comptés, pour le résumé. */
  label?: string;
  className?: string;
};

/** Pages affichées autour de la page courante, en plus de la première et de la dernière. */
const WINDOW = 1;

/**
 * Pagination compacte. Au-delà de quelques pages, la liste est fenêtrée autour
 * de la page courante (`1 … 4 5 6 … 20`) : une table de 50 pages n'affiche pas
 * 50 boutons.
 */
export function Pagination({
  page,
  total,
  limit,
  onChange,
  label = 'éléments',
  className,
}: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / Math.max(limit, 1)));
  const current = Math.min(Math.max(page, 1), totalPages);

  // Sans résultat, « Affichage 1–0 sur 0 » n'a pas de sens.
  const premier = total === 0 ? 0 : (current - 1) * limit + 1;
  const dernier = Math.min(current * limit, total);

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-card border border-border-subtle bg-surface px-4 py-3',
        'text-xs text-content-muted sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
    >
      <p aria-live="polite">
        {total === 0 ? (
          `Aucun ${label.replace(/s$/, '')}`
        ) : (
          <>
            Affichage <span className="font-semibold text-content">{premier}</span>–
            <span className="font-semibold text-content">{dernier}</span> sur{' '}
            <span className="font-semibold text-content">{total}</span> {label}
          </>
        )}
      </p>

      {totalPages > 1 ? (
        <nav aria-label="Pagination" className="flex items-center gap-1">
          <PageButton
            aria-label="Page précédente"
            disabled={current === 1}
            onClick={() => onChange(current - 1)}
          >
            <MdChevronLeft className="size-4" />
          </PageButton>

          {pagesToShow(current, totalPages).map((item, index) =>
            item === 'ellipsis' ? (
              <span
                key={`ellipsis-${index}`}
                aria-hidden="true"
                className="px-1 text-content-subtle"
              >
                …
              </span>
            ) : (
              <PageButton
                key={item}
                aria-label={`Page ${item}`}
                aria-current={item === current ? 'page' : undefined}
                active={item === current}
                onClick={() => onChange(item)}
              >
                {item}
              </PageButton>
            ),
          )}

          <PageButton
            aria-label="Page suivante"
            disabled={current === totalPages}
            onClick={() => onChange(current + 1)}
          >
            <MdChevronRight className="size-4" />
          </PageButton>
        </nav>
      ) : null}
    </div>
  );
}

type PageButtonProps = {
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
  'aria-label'?: string;
  'aria-current'?: 'page';
};

function PageButton({ active, disabled, onClick, children, ...aria }: PageButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex h-8 min-w-8 items-center justify-center rounded-lg border px-2 text-xs font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/30',
        active
          ? 'border-brand-600 bg-brand-600 text-white'
          : 'border-border-subtle text-content hover:bg-surface-muted',
        disabled && 'cursor-not-allowed opacity-40 hover:bg-transparent',
      )}
      {...aria}
    >
      {children}
    </button>
  );
}

/**
 * Numéros à afficher : la première page, la dernière, et une fenêtre autour de
 * la page courante. Les trous deviennent des points de suspension.
 */
function pagesToShow(current: number, totalPages: number): (number | 'ellipsis')[] {
  const pages = new Set<number>([1, totalPages]);
  for (let offset = -WINDOW; offset <= WINDOW; offset += 1) {
    const page = current + offset;
    if (page >= 1 && page <= totalPages) pages.add(page);
  }

  const ordered = [...pages].sort((a, b) => a - b);
  const result: (number | 'ellipsis')[] = [];

  ordered.forEach((page, index) => {
    const previous = ordered[index - 1];
    // Un seul numéro manquant : on l'affiche plutôt qu'un « … » plus large que lui.
    if (previous !== undefined && page - previous === 2) result.push(page - 1);
    else if (previous !== undefined && page - previous > 2) result.push('ellipsis');
    result.push(page);
  });

  return result;
}
